/**
 * Settings → Features
 *
 * Tenant-admin surface for browsing every feature the product knows about
 * and toggling the ones gated per-tenant. Reads from the FeaturesProvider
 * context (so it shares cache with the rest of the app) and PATCHes the
 * features API on toggle, splicing the response back into the cache for
 * an immediate UI update.
 *
 * Design notes
 * ------------
 * - We intentionally show every registered feature, even already-on GA
 *   features, so admins can see the full surface area in one place. The
 *   alternative (hide GA features) hides the very things admins ask
 *   support about ("where do I disable feature X?").
 * - Beta + Deprecated lifecycle states get a chip; GA shows nothing,
 *   keeping the page calm.
 * - Dependency hints surface when a feature can't be enabled because of
 *   an unmet `requires_features`.
 * - Non-admin users still see the page — via the sidebar route — but the
 *   toggle is disabled with a tooltip. The backend rejects the PATCH for
 *   non-admins, so this is purely cosmetic.
 */
import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Switch,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import ScienceRounded from '@mui/icons-material/ScienceRounded';
import FlagRounded from '@mui/icons-material/FlagRounded';
import { SnackbarContext } from '../../ui/SnackbarProvider';
import { useFeatures } from '../../ui/FeaturesProvider';
import { patchFeature, FeatureRead } from '../../services/featuresService';
import { getTenantIdFromStorage } from '../../services/tenantSettingsService';
import SettingsPageHeader from './components/SettingsPageHeader';
import { useCan } from '../../ui/PermissionsProvider';

const CATEGORY_LABEL: Record<FeatureRead['category'], string> = {
  ai_capabilities: 'AI capabilities',
  data: 'Data',
  admin: 'Admin',
  integrations: 'Integrations',
  experimental: 'Experimental',
};

const CATEGORY_ORDER: FeatureRead['category'][] = [
  'ai_capabilities',
  'data',
  'integrations',
  'admin',
  'experimental',
];


interface RowProps {
  feature: FeatureRead;
  canEdit: boolean;
  busy: boolean;
  onToggle: (feature: FeatureRead, next: boolean) => void;
}

function FeatureRow({ feature, canEdit, busy, onToggle }: RowProps) {
  const blockedByDeps =
    !feature.dependencies_satisfied && feature.requires_features.length > 0;

  const tooltip = !canEdit
    ? 'Admin role required to change this'
    : blockedByDeps
      ? `Enable required features first: ${feature.requires_features.join(', ')}`
      : '';

  // The switch reflects `explicit_enabled` (the toggle the admin set), not
  // the effective `enabled` state — so an admin can flip a feature on
  // before its dependency is enabled and the chip will update later.
  const switchValue = feature.explicit_enabled;

  return (
    <Box
      sx={(t) => ({
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        py: 1.75,
        borderBottom: `1px solid ${t.tokens.border.subtle}`,
        '&:last-child': { borderBottom: 'none' },
      })}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
            {feature.name}
          </Typography>
          {feature.lifecycle === 'beta' && (
            <Chip
              size="small"
              label="Beta"
              color="info"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
          {feature.lifecycle === 'deprecated' && (
            <Chip
              size="small"
              label="Deprecated"
              color="warning"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
          {feature.enabled && feature.lifecycle !== 'deprecated' && (
            <Chip
              size="small"
              label="On"
              color="success"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
        </Stack>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: '0.8125rem', lineHeight: 1.5 }}
        >
          {feature.description}
        </Typography>
        {blockedByDeps && (
          <Typography
            variant="caption"
            color="warning.main"
            sx={{ mt: 0.5, display: 'block', fontSize: '0.75rem' }}
          >
            Requires: {feature.requires_features.join(', ')}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 0.25 }}>
        {busy && <CircularProgress size={16} />}
        <Tooltip title={tooltip} placement="left">
          <span>
            <Switch
              checked={switchValue}
              disabled={!canEdit || busy}
              onChange={(_e, next) => onToggle(feature, next)}
              inputProps={{ 'aria-label': `Toggle ${feature.name}` }}
            />
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default function FeaturesSettingsPage() {
  const { features, loading, error, setFeatureLocal, refreshFeatures } = useFeatures();
  const { notify } = React.useContext(SnackbarContext);
  const tenantId = React.useMemo(() => getTenantIdFromStorage(), []);
  const canEdit = useCan('manage_features');

  const [busyKey, setBusyKey] = React.useState<string | null>(null);

  const handleToggle = React.useCallback(
    async (feature: FeatureRead, next: boolean) => {
      if (!tenantId) {
        notify('Tenant context missing', 'error');
        return;
      }
      setBusyKey(feature.key);
      try {
        const updated = await patchFeature(tenantId, feature.key, {
          enabled: next,
          reason: next ? 'Enabled from Settings' : 'Disabled from Settings',
        });
        setFeatureLocal(updated);
        notify(
          `${feature.name} ${updated.enabled ? 'enabled' : 'disabled'}`,
          'success',
        );
      } catch (e: any) {
        notify(e?.message || `Failed to update ${feature.name}`, 'error');
        // Refetch so the UI re-syncs with whatever the server has, in case
        // we missed a concurrent change.
        await refreshFeatures();
      } finally {
        setBusyKey(null);
      }
    },
    [tenantId, notify, setFeatureLocal, refreshFeatures],
  );

  // Group by category, preserving registry order within each group (the
  // backend already returns features in registry insertion order).
  const grouped = React.useMemo(() => {
    const out: Record<FeatureRead['category'], FeatureRead[]> = {
      ai_capabilities: [],
      data: [],
      integrations: [],
      admin: [],
      experimental: [],
    };
    for (const f of features) out[f.category].push(f);
    return out;
  }, [features]);

  return (
    <Box sx={{ width: '100%' }}>
      <SettingsPageHeader description="Toggle product capabilities for your workspace. Beta features are off by default; turn them on to give your team early access." />

      {!canEdit && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You're viewing this page in read-only mode. Ask an admin to change feature state.
        </Alert>
      )}

      {error && !loading && features.length === 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && features.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={24} />
        </Box>
      ) : features.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              No features available.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        CATEGORY_ORDER.map((cat) => {
          const list = grouped[cat];
          if (!list || list.length === 0) return null;
          return (
            <Card key={cat} sx={{ mb: 3 }}>
              <CardContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
                  <Box
                    sx={(t) => ({
                      width: 28,
                      height: 28,
                      borderRadius: `${t.ds.radius.sm}px`,
                      bgcolor: t.tokens.brand.primarySoft,
                      color: t.tokens.brand.primary,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    })}
                  >
                    {cat === 'experimental' ? (
                      <ScienceRounded sx={{ fontSize: 16 }} />
                    ) : (
                      <FlagRounded sx={{ fontSize: 16 }} />
                    )}
                  </Box>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '0.95rem' }}>
                    {CATEGORY_LABEL[cat]}
                  </Typography>
                </Box>
                {list.map((feature) => (
                  <FeatureRow
                    key={feature.key}
                    feature={feature}
                    canEdit={canEdit}
                    busy={busyKey === feature.key}
                    onToggle={handleToggle}
                  />
                ))}
              </CardContent>
            </Card>
          );
        })
      )}
    </Box>
  );
}
